import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUpIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '@/utils/constants';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      // TODO: Implement newsletter subscription
      setSubscribed(true);
      setTimeout(() => {
        setEmail('');
        setSubscribed(false);
      }, 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-dark-900 dark:bg-dark-950 text-gray-300 border-t border-dark-700">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-primary-600 to-accent-600 dark:from-primary-700 dark:to-accent-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold text-white mb-2">
                Subscribe to our Newsletter
              </h3>
              <p className="text-primary-50">
                Get exclusive deals, product updates, and eco-tips delivered to your inbox
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-80">
                <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className={cn(
                    'w-full h-12 pl-10 pr-4 rounded-lg',
                    'bg-white dark:bg-dark-800',
                    'border-0 focus:ring-2 focus:ring-white/50',
                    'text-gray-900 dark:text-white placeholder-gray-500',
                    'transition-all duration-200'
                  )}
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                size="lg"
                className="bg-dark-900 hover:bg-dark-800 text-white whitespace-nowrap"
              >
                {subscribed ? 'Subscribed!' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 relative flex items-center justify-center">
                {/* Trash Can Logo with Sparkles */}
                <svg
                  viewBox="0 0 64 64"
                  className="w-full h-full"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <filter id="glow-footer">
                      <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Bin Body - Dark Teal Grey */}
                  <rect x="14" y="24" width="36" height="32" rx="2" fill="#2d4a52" stroke="#1e2d33" strokeWidth="1.5"/>

                  {/* Vertical Blue Stripes/Ridges with Glow */}
                  <rect x="18" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.9"/>
                  <rect x="24" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.9"/>
                  <rect x="30" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.9"/>
                  <rect x="36" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.9"/>
                  <rect x="42" y="26" width="3" height="28" rx="0.5" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.9"/>

                  {/* Bin Lid - Dark Teal Grey, Slightly Ajar */}
                  <rect x="10" y="18" width="44" height="8" rx="2" fill="#2d4a52" stroke="#1e2d33" strokeWidth="1.5" transform="rotate(-8 32 22)"/>

                  {/* Lid Opening - Blue Interior */}
                  <ellipse cx="32" cy="22" rx="18" ry="3" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.7"/>

                  {/* Sparkles - Four Pointed Stars */}
                  <g filter="url(#glow-footer)">
                    {/* Large Sparkle */}
                    <path d="M 40 8 L 41 11 L 44 10 L 41 12 L 42 15 L 40 13 L 38 15 L 39 12 L 36 10 L 39 11 Z" fill="#60a5fa" opacity="0.9"/>
                    {/* Small Sparkle */}
                    <path d="M 48 4 L 48.5 5.5 L 50 5 L 48.5 6 L 49 7.5 L 48 6.5 L 47 7.5 L 47.5 6 L 46 5 L 47.5 5.5 Z" fill="#60a5fa" opacity="0.9"/>
                  </g>

                  {/* Dots */}
                  <circle cx="45" cy="6" r="1.5" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.8"/>
                  <circle cx="50" cy="8" r="1" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.8"/>
                  <circle cx="43" cy="10" r="1" fill="#60a5fa" filter="url(#glow-footer)" opacity="0.8"/>

                  {/* Base/Shadow */}
                  <ellipse cx="32" cy="58" rx="20" ry="2" fill="#1e2d33" opacity="0.6"/>
                </svg>
              </div>
              <span className="ml-2 text-2xl font-bold bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">
                RecyclerBin
              </span>
            </div>
            <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
              Your sustainable marketplace for new and recycled products.
              Join us in building a greener future through conscious consumption.
            </p>

            {/* Social Links */}
            <div className="flex space-x-3 mb-6">
              {[
                { name: 'Facebook', path: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z' },
                { name: 'Twitter', path: 'M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84' },
                { name: 'Instagram', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z' },
                { name: 'LinkedIn', path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
              ].map((social) => (
                <a
                  key={social.name}
                  href="#"
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    'bg-dark-800 dark:bg-dark-900',
                    'text-gray-400 hover:text-white',
                    'hover:bg-primary-500 hover:scale-110',
                    'transition-all duration-200'
                  )}
                >
                  <span className="sr-only">{social.name}</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d={social.path} />
                  </svg>
                </a>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm">
                <svg className="h-5 w-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-gray-400">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <svg className="h-5 w-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-400">Quality Guaranteed</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">Shop</h3>
            <ul className="space-y-3">
              {[
                { label: 'All Products', path: ROUTES.PRODUCTS },
                { label: 'New Arrivals', path: ROUTES.PRODUCTS + '?sort=newest' },
                { label: 'Best Sellers', path: ROUTES.PRODUCTS + '?sort=popular' },
                { label: 'Deals & Offers', path: ROUTES.PRODUCTS + '?deals=true' },
                { label: 'Recycled Products', path: ROUTES.PRODUCTS + '?type=recycled' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-primary-500 hover:translate-x-1 inline-block transition-all duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">Customer Service</h3>
            <ul className="space-y-3">
              {[
                { label: 'Help Center', path: '#' },
                { label: 'Track Order', path: '#' },
                { label: 'Shipping Info', path: '#' },
                { label: 'Returns & Refunds', path: '#' },
                { label: 'Contact Us', path: ROUTES.CONTACT },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.path}
                    className="text-sm text-gray-400 hover:text-primary-500 hover:translate-x-1 inline-block transition-all duration-200"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* About & Legal */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">About</h3>
            <ul className="space-y-3">
              {[
                { label: 'About Us', path: ROUTES.ABOUT },
                { label: 'Our Mission', path: '#' },
                { label: 'Sustainability', path: '#' },
                { label: 'Careers', path: '#' },
                { label: 'Privacy Policy', path: '#' },
                { label: 'Terms of Service', path: '#' },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.path}
                    className="text-sm text-gray-400 hover:text-primary-500 hover:translate-x-1 inline-block transition-all duration-200"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="border-t border-dark-700 pt-8 pb-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-3">We Accept</h4>
              <div className="flex flex-wrap gap-3">
                {['Visa', 'Mastercard', 'PayPal', 'AmEx', 'Discover'].map((payment) => (
                  <div
                    key={payment}
                    className="px-4 py-2 bg-white dark:bg-dark-800 rounded border border-gray-200 dark:border-dark-700"
                  >
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-400">
                      {payment}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              className={cn(
                'flex items-center gap-2 px-6 py-3',
                'bg-dark-800 dark:bg-dark-900 hover:bg-primary-500',
                'text-gray-300 hover:text-white',
                'rounded-lg border border-dark-700',
                'transition-all duration-200',
                'hover:scale-105'
              )}
            >
              <ChevronUpIcon className="h-5 w-5" />
              <span className="text-sm font-medium">Back to Top</span>
            </button>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-dark-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} RecyclerBin. All rights reserved.
            </p>
            <p className="text-sm text-gray-400 flex items-center gap-1.5">
              Made with
              <span className="text-red-500 animate-pulse">❤️</span>
              for a sustainable future
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
